import { mapEase } from "./ease";
import { scheduleNext } from "./timing";

// Grow animation: scales element from a starting scale (default 0) up to target (default 1)
// loopBehavior: once | infinite | sporadic (sporadic triggers random single grow cycles)
export const growAnim = (
	element,
	{
		gsap,
		loopBehavior = "infinite",
		animatedElements,
		sporadicTimers,
		duration = 1,
		ease = "none",
		fromScale = 0,
		toScale = 1,
		randomDelayMin = 3000,
		randomDelayMax = 8000,
	}
) => {
	if (!element || !gsap || !animatedElements) return null;
	if (animatedElements.has(element)) return animatedElements.get(element);
	const mappedEase = mapEase(ease);

	const startSingleCycle = () =>
		gsap.fromTo(
			element,
			{ scale: fromScale },
			{
				scale: toScale,
				duration,
				ease: mappedEase,
				transformOrigin: "center center",
				onComplete: () => {
					if (loopBehavior === "sporadic" && animatedElements.has(element)) {
						animatedElements.set(element, null);
						scheduleNext({
							element,
							animatedElements,
							sporadicTimers,
							startSingleCycle,
							randomDelayMin,
							randomDelayMax,
						});
					}
				},
			}
		);

	switch (loopBehavior) {
		case "once": {
			const anim = startSingleCycle();
			animatedElements.set(element, anim);
			return anim;
		}
		case "infinite": {
			// continuous pulse: grow then shrink
			const anim = gsap.fromTo(
				element,
				{ scale: fromScale },
				{
					scale: toScale,
					duration,
					ease: mappedEase,
					repeat: -1,
					yoyo: true,
					transformOrigin: "center center",
				}
			);
			animatedElements.set(element, anim);
			return anim;
		}
		case "sporadic": {
			animatedElements.set(element, null);
			scheduleNext({
				element,
				animatedElements,
				sporadicTimers,
				startSingleCycle,
				randomDelayMin,
				randomDelayMax,
			});
			return null;
		}
		default: {
			const anim = startSingleCycle();
			animatedElements.set(element, anim);
			return anim;
		}
	}
};

// (stopGrowAnim removed; use stopManagedAnimation from timing.js directly if needed)

export const growPreview = (
	element,
	{ gsap, duration = 2, ease = "power2.inOut", fromScale = 0, toScale = 1, transformOrigin = "center center" } = {}
) => {
	if (!element || !gsap) return null;
	return gsap.fromTo(
		element,
		{ scale: fromScale },
		{ scale: toScale, duration, ease: mapEase(ease), transformOrigin }
	);
};
